import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { toast } from "react-toastify";
import { fetchReservations } from "../../../services/ReservationService";
import "./ReservationCalendar.css";

/** Calendar for table reservations only (V2) — never loads event bookings. */
const STATUS_COLORS = {
  pending: { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
  confirmed: { bg: "#d1fae5", border: "#10b981", text: "#065f46" },
  arrived: { bg: "#bae6fd", border: "#0ea5e9", text: "#075985" },
  completed: { bg: "#e2e8f0", border: "#64748b", text: "#334155" },
  cancelled: { bg: "#fecaca", border: "#ef4444", text: "#991b1b" },
  "no-show": { bg: "#ede9fe", border: "#8b5cf6", text: "#5b21b6" },
};

const LEGEND_STATUSES = [
  "pending",
  "confirmed",
  "arrived",
  "completed",
  "cancelled",
  "no-show",
];

const toDateKey = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export default function ReservationCalendar({ open, onClose, onSelectReservation }) {
  const { t, i18n } = useTranslation();
  const calendarRef = useRef(null);
  const rangeRef = useRef({ from: "", to: "" });
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewLabel, setViewLabel] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const loadRange = useCallback(
    async (from, to) => {
      if (!from || !to) return;
      if (rangeRef.current.from === from && rangeRef.current.to === to) return;
      rangeRef.current = { from, to };
      setLoading(true);
      try {
        const data = await fetchReservations({ from, to });
        setReservations(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t("reservationLoadError")));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const events = useMemo(
    () =>
      reservations
        .filter((item) => item?.startAt)
        .map((item) => {
          const status = item.status || "pending";
          const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
          const tableLabel =
            item.table?.name ||
            (item.tableNumber
              ? `${t("reservationTable")} ${item.tableNumber}`
              : t("reservationTableUnassigned"));
          const party = item.partySize || 0;
          const title = `${item.customerName || t("reservationCustomer")} · ${party}`;

          return {
            id: String(item._id),
            title,
            start: item.startAt,
            end: item.endAt || undefined,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            textColor: colors.text,
            extendedProps: {
              reservation: item,
              status,
              tableLabel,
              partySize: party,
            },
          };
        }),
    [reservations, t]
  );

  const stats = useMemo(() => {
    const counts = {
      total: reservations.length,
      pending: 0,
      confirmed: 0,
      arrived: 0,
      completed: 0,
      cancelled: 0,
      "no-show": 0,
    };
    reservations.forEach((item) => {
      const status = item.status || "pending";
      if (Object.prototype.hasOwnProperty.call(counts, status)) {
        counts[status] += 1;
      }
    });
    return counts;
  }, [reservations]);

  const handleDatesSet = useCallback(
    (info) => {
      setViewLabel(info.view.title);
      const from = toDateKey(info.start);
      const endInclusive = new Date(info.end);
      endInclusive.setDate(endInclusive.getDate() - 1);
      const to = toDateKey(endInclusive);
      loadRange(from, to);
    },
    [loadRange]
  );

  const handleEventClick = useCallback(
    (info) => {
      const reservation = info.event.extendedProps?.reservation;
      if (reservation && onSelectReservation) {
        onSelectReservation(reservation);
      }
    },
    [onSelectReservation]
  );

  const renderEventContent = useCallback((eventInfo) => {
    const { status, tableLabel, partySize } = eventInfo.event.extendedProps || {};
    const isMonth = eventInfo.view.type === "dayGridMonth";
    const timeText = eventInfo.timeText;

    return (
      <div className={`reservation-cal-event is-${status || "pending"}`}>
        {!isMonth && timeText ? (
          <span className="reservation-cal-event__time">{timeText}</span>
        ) : null}
        {isMonth && timeText ? (
          <span className="reservation-cal-event__dot" aria-hidden="true" />
        ) : null}
        <span className="reservation-cal-event__title">{eventInfo.event.title}</span>
        {!isMonth ? (
          <span className="reservation-cal-event__meta">
            {tableLabel}
            {partySize ? ` · ${partySize}` : ""}
          </span>
        ) : null}
      </div>
    );
  }, []);

  if (!open) return null;

  return (
    <div
      className="reservation-popup-backdrop reservation-cal-backdrop"
      onMouseDown={onClose}
    >
      <div
        className="reservation-popup reservation-cal-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reservation-calendar-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="reservation-cal-header">
          <div className="reservation-cal-header__brand">
            <span className="reservation-cal-header__icon" aria-hidden="true">
              <i className="fa fa-calendar-alt" />
            </span>
            <div>
              <h2 id="reservation-calendar-title">{t("reservationCalendarTitle")}</h2>
              <p>{t("reservationCalendarHint")}</p>
            </div>
          </div>
          <button
            type="button"
            className="reservation-cal-close"
            onClick={onClose}
            aria-label={t("close")}
          >
            <i className="fa fa-times" />
          </button>
        </header>

        <div className="reservation-cal-toolbar">
          <div className="reservation-cal-toolbar__title">
            <strong>{viewLabel}</strong>
            {loading ? (
              <span className="reservation-cal-loading">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                {t("loading")}
              </span>
            ) : (
              <span className="reservation-cal-toolbar__count">
                {t("reservationCalendarCount", { count: stats.total })}
              </span>
            )}
          </div>
          <div className="reservation-cal-stats">
            {LEGEND_STATUSES.map((status) => (
              <span key={status} className={`reservation-cal-stat is-${status}`}>
                <i className="fa fa-circle" aria-hidden="true" />
                {stats[status] || 0} {t(`reservationStatus_${status}`)}
              </span>
            ))}
          </div>
        </div>

        <div className="reservation-cal-body">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            buttonText={{
              today: t("reservationCalendarToday"),
              month: t("reservationCalendarMonth"),
              week: t("reservationCalendarWeek"),
              day: t("reservationCalendarDay"),
            }}
            locale={i18n.language || "en"}
            height="auto"
            contentHeight={520}
            stickyHeaderDates
            nowIndicator
            dayMaxEvents={4}
            moreLinkClick="popover"
            events={events}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            slotMinTime="08:00:00"
            slotMaxTime="24:00:00"
            allDaySlot={false}
            eventDisplay="block"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
            }}
          />
        </div>

        <footer className="reservation-cal-footer">
          <p>
            <i className="fa fa-info-circle" aria-hidden="true" />
            {t("reservationCalendarFooter")}
          </p>
        </footer>
      </div>
    </div>
  );
}
